import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProducts } from "../hooks/useProducts";
import ProductCard from "../components/ui/ProductCard";
import Spinner from "../components/ui/Spinner";
import ProductFilters from "../components/ui/ProductFilters";

export default function Shop() {
    const [searchParams] = useSearchParams();
    const [filtersOpen, setFiltersOpen] = useState(false);

    const {
        products,
        loading,
        error,
        totalPages,
        total,
        filters,
        updateFilters,
        setPage,
    } = useProducts({
        search: searchParams.get("search") || "",
        category: searchParams.get("category") || "",
    });

    return (
        <div className="section">
            <div className="container">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="section-title">
                        {filters.search
                            ? `Results for "${filters.search}"`
                            : "All Products"}
                    </h1>
                    <p className="section-subtitle mb-0">
                        {loading ? "Loading..." : `${total} products found`}
                    </p>
                </div>

                <div className="flex gap-8">
                    {/* Sidebar Filters — Desktop */}
                    <aside className="hidden lg:block w-60 shrink-0">
                        <ProductFilters
                            filters={filters}
                            onUpdate={updateFilters}
                        />
                    </aside>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                            {/* Mobile filter toggle */}
                            <button
                                onClick={() => setFiltersOpen(true)}
                                className="lg:hidden btn-secondary text-sm py-2 flex items-center gap-2"
                            >
                                <FilterIcon /> Filters
                            </button>

                            {/* Sort */}
                            <div className="flex items-center gap-2 ml-auto">
                                <label className="text-sm text-neutral-500 shrink-0">
                                    Sort by:
                                </label>
                                <select
                                    className="input w-auto text-sm py-1.5"
                                    value={`${filters.orderby}_${filters.order}`}
                                    onChange={(e) => {
                                        const [orderby, order] =
                                            e.target.value.split("_");
                                        updateFilters({ orderby, order });
                                    }}
                                >
                                    <option value="date_desc">Newest</option>
                                    <option value="date_asc">Oldest</option>
                                    <option value="price_asc">
                                        Price: Low to High
                                    </option>
                                    <option value="price_desc">
                                        Price: High to Low
                                    </option>
                                    <option value="title_asc">Name: A–Z</option>
                                    <option value="title_desc">
                                        Name: Z–A
                                    </option>
                                    <option value="popularity_desc">
                                        Most Popular
                                    </option>
                                    <option value="rating_desc">
                                        Top Rated
                                    </option>
                                </select>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="relative mb-6">
                            <input
                                type="text"
                                className="input pl-10"
                                placeholder="Search products..."
                                defaultValue={filters.search}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                        updateFilters({
                                            search: e.target.value,
                                        });
                                }}
                            />
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                                />
                            </svg>
                        </div>

                        {/* Active Filters */}
                        <ActiveFilters
                            filters={filters}
                            onUpdate={updateFilters}
                        />

                        {/* Products Grid */}
                        {loading ? (
                            <Spinner />
                        ) : error ? (
                            <ErrorState message={error} />
                        ) : products.length === 0 ? (
                            <EmptyState
                                onClear={() =>
                                    updateFilters({
                                        search: "",
                                        category: "",
                                        minPrice: "",
                                        maxPrice: "",
                                    })
                                }
                            />
                        ) : (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                                    {products.map((product) => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                        />
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <Pagination
                                        current={filters.page}
                                        total={totalPages}
                                        onPageChange={setPage}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filters Drawer */}
            {filtersOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setFiltersOpen(false)}
                    />
                    <div className="relative ml-auto w-72 bg-white h-full overflow-y-auto p-6 shadow-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-bold text-neutral-900">
                                Filters
                            </h3>
                            <button
                                onClick={() => setFiltersOpen(false)}
                                className="p-1 text-neutral-500 hover:text-neutral-800"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <ProductFilters
                            filters={filters}
                            onUpdate={(f) => {
                                updateFilters(f);
                                setFiltersOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Active Filter Pills ───────────────────────────────────────────
function ActiveFilters({ filters, onUpdate }) {
    const active = [];
    if (filters.search)
        active.push({ key: "search", label: `"${filters.search}"` });
    if (filters.minPrice)
        active.push({ key: "minPrice", label: `Min $${filters.minPrice}` });
    if (filters.maxPrice)
        active.push({ key: "maxPrice", label: `Max $${filters.maxPrice}` });

    if (active.length === 0) return null;

    return (
        <div className="flex flex-wrap gap-2 mb-4">
            {active.map((f) => (
                <span
                    key={f.key}
                    className="flex items-center gap-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded-full px-3 py-1 font-medium"
                >
                    {f.label}
                    <button
                        onClick={() => onUpdate({ [f.key]: "" })}
                        className="ml-1 hover:text-primary-900"
                    >
                        ✕
                    </button>
                </span>
            ))}
            <button
                onClick={() =>
                    onUpdate({
                        search: "",
                        minPrice: "",
                        maxPrice: "",
                        category: "",
                    })
                }
                className="text-xs text-neutral-400 hover:text-neutral-700 underline"
            >
                Clear all
            </button>
        </div>
    );
}

// ── Pagination ────────────────────────────────────────────────────
function Pagination({ current, total, onPageChange }) {
    const pages = [];
    const delta = 2;

    for (let i = 1; i <= total; i++) {
        if (
            i === 1 ||
            i === total ||
            (i >= current - delta && i <= current + delta)
        ) {
            pages.push(i);
        } else if (pages[pages.length - 1] !== "...") {
            pages.push("...");
        }
    }

    return (
        <div className="flex justify-center items-center gap-1 mt-10">
            <PageBtn
                disabled={current === 1}
                onClick={() => onPageChange(current - 1)}
            >
                ‹
            </PageBtn>
            {pages.map((p, i) =>
                p === "..." ? (
                    <span
                        key={`dots-${i}`}
                        className="px-2 text-neutral-400 text-sm"
                    >
                        …
                    </span>
                ) : (
                    <PageBtn
                        key={p}
                        active={p === current}
                        onClick={() => onPageChange(p)}
                    >
                        {p}
                    </PageBtn>
                ),
            )}
            <PageBtn
                disabled={current === total}
                onClick={() => onPageChange(current + 1)}
            >
                ›
            </PageBtn>
        </div>
    );
}

function PageBtn({ children, active, disabled, onClick }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors cursor-pointer
        ${active ? "bg-primary-600 text-white" : ""}
        ${!active && !disabled ? "text-neutral-600 hover:bg-neutral-100" : ""}
        ${disabled ? "text-neutral-300 cursor-not-allowed" : ""}
      `}
        >
            {children}
        </button>
    );
}

// ── Empty & Error States ──────────────────────────────────────────
function EmptyState({ onClear }) {
    return (
        <div className="text-center py-20">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-16 h-16 mx-auto text-neutral-200 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
            </svg>
            <h3 className="text-lg font-semibold text-neutral-700 mb-2">
                No products found
            </h3>
            <p className="text-neutral-400 text-sm mb-6">
                Try adjusting your filters or search term.
            </p>
            <button onClick={onClear} className="btn-primary">
                Clear Filters
            </button>
        </div>
    );
}

function ErrorState({ message }) {
    return (
        <div className="text-center py-20">
            <p className="text-red-500 font-medium">{message}</p>
        </div>
    );
}

function FilterIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
            />
        </svg>
    );
}
